import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RecaptchaVerifier } from "firebase/auth";
import { auth } from "../../../firebase";
import {
  sendOTP,
  verifyOTP,
  completeRegistration,
  clearAuthState,
} from "../../../slice/auth/authSlice";
import logo from "../../../assets/logo.png";

const LoginRegister = ({ onClose, onLoginSuccess }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    loading,
    error,
    isSuccess,
    otpSent,
    otpVerified,
    isNewUser,
    verificationId,
    phoneNumber,
    firebaseUid,
    firebaseToken,
  } = useSelector((state) => state.auth);

  const [form, setForm] = useState({
    phoneNumber: "",
    otp: "",
    name: "",
    email: "",
  });

  const [resendTimer, setResendTimer] = useState(0);
  const recaptchaVerifierRef = useRef(null);

  // ✅ Initialize reCAPTCHA ONCE
  useEffect(() => {
    if (!recaptchaVerifierRef.current) {
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          {
            size: "invisible",
            callback: () => {
              console.log("✅ reCAPTCHA solved");
            },
            "expired-callback": () => {
              console.log("⚠️ reCAPTCHA expired");
            },
          }
        );
        
        recaptchaVerifierRef.current = window.recaptchaVerifier;
        console.log("✅ reCAPTCHA initialized");
      } catch (err) {
        console.error("❌ reCAPTCHA init error:", err);
      }
    }

    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          console.log("Recaptcha already cleared");
        }
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  // ✅ Resend Timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ SEND OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();

    if (!recaptchaVerifierRef.current) {
      alert("Please wait for reCAPTCHA to load");
      return;
    }

    const formattedPhone = form.phoneNumber.startsWith("+")
      ? form.phoneNumber
      : `+91${form.phoneNumber}`;

    dispatch(
      sendOTP({
        phoneNumber: formattedPhone,
        recaptchaVerifier: recaptchaVerifierRef.current,
      })
    );

    // Start 60 second timer
    setResendTimer(60);
  };

  // ✅ RESEND OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    if (!recaptchaVerifierRef.current) {
      alert("Please wait for reCAPTCHA to load");
      return;
    }

    const formattedPhone = form.phoneNumber.startsWith("+")
      ? form.phoneNumber
      : `+91${form.phoneNumber}`;

    dispatch(
      sendOTP({
        phoneNumber: formattedPhone,
        recaptchaVerifier: recaptchaVerifierRef.current,
      })
    );

    // Start 60 second timer
    setResendTimer(60);
  };

  // ✅ VERIFY OTP
  const handleVerifyOTP = (e) => {
    e.preventDefault();

    if (!form.otp || form.otp.length !== 6) {
      alert("Please enter a 6-digit OTP");
      return;
    }

    dispatch(
      verifyOTP({
        verificationId,
        otpCode: form.otp,
        phoneNumber,
      })
    );
  };

  // ✅ COMPLETE REGISTRATION (for new users)
  const handleCompleteRegistration = (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      alert("Please enter your name");
      return;
    }

    if (form.name.trim().length < 2) {
      alert("Name must be at least 2 characters");
      return;
    }

    if (!form.email.trim()) {
      alert("Please enter your email");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      alert("Please enter a valid email address");
      return;
    }

    dispatch(
      completeRegistration({
        name: form.name.trim(),
        email: form.email.trim(),
        phoneNumber,
        firebaseUid,
        firebaseToken,
      })
    );
  };


useEffect(() => {
  if (isSuccess) {
    dispatch(clearAuthState());
    if (onClose) onClose();
    if (onLoginSuccess) {
      onLoginSuccess();
    } else {
      navigate("/");
    }
  }
}, [isSuccess, navigate, dispatch, onClose, onLoginSuccess]);

  // ✅ Back to phone
  const handleBackToPhone = () => {
    dispatch(clearAuthState());
    setForm({ phoneNumber: "", otp: "", name: "", email: "" });
    setResendTimer(0);
    window.location.reload();
  };

  return (
    <div className="page-content">
      <section className="">
        <div className="row justify-content-center align-items-center" style={{position:"relative"}}>
         
          <div className="col-lg-12">
               <button 
                onClick={onClose}
               style={{
            background: "none", border: "none", fontSize: "40px",
            cursor: "pointer", color: "red", lineHeight: 1,position:"absolute",right:"30px",top:"5px"
          }}>×</button>
            <div className="login-area noBORDER">
              <img src={logo} alt="" className="formlogo" />

              <h2 className="text-center mb-4">
                {otpVerified && isNewUser
                  ? "Complete Registration"
                  : "User Login"}
              </h2>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {/* STEP 1: SEND OTP FORM (Phone Number) */}
              {!otpSent && !otpVerified && (
                <form onSubmit={handleSendOTP}>
                  {/* Invisible reCAPTCHA container */}
                  <div id="recaptcha-container"></div>

                  <input
                    type="tel"
                    name="phoneNumber"
                    className="form-control mb-3"
                    placeholder="Phone Number"
                    value={form.phoneNumber}
                    onChange={handleChange}
                    maxLength="10"
                    required
                  />

                  <button
                    type="submit"
                    className="btn btn-secondary w-100 whitT"
                    disabled={loading}
                  >
                    {loading ? "Sending OTP..." : "Send OTP"}
                  </button>
                </form>
              )}

              {/* STEP 2: VERIFY OTP FORM */}
              {otpSent && !otpVerified && (
                <form onSubmit={handleVerifyOTP}>
                  <div className="alert alert-info" role="alert">
                    📱 OTP sent to {phoneNumber}
                  </div>

                  <input
                    type="text"
                    name="otp"
                    className="form-control mb-3"
                    placeholder="Enter 6-digit OTP"
                    value={form.otp}
                    onChange={handleChange}
                    maxLength="6"
                    autoFocus
                    required
                  />

                  <button
                    type="submit"
                    className="btn btn-secondary w-100 whitT"
                    disabled={loading}
                  >
                    {loading ? "Verifying..." : "Verify OTP"}
                  </button>

                  {/* RESEND OTP BUTTON */}
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 mt-2"
                    onClick={handleResendOTP}
                    disabled={loading || resendTimer > 0}
                  >
                    {resendTimer > 0
                      ? `Resend OTP in ${resendTimer}s`
                      : "Resend OTP"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-link w-100 mt-2"
                    onClick={handleBackToPhone}
                    disabled={loading}
                  >
                    ← Change mobile Number
                  </button>
                </form>
              )}

              {/* STEP 3: COMPLETE REGISTRATION (New Users Only) */}
              {otpVerified && isNewUser && (
                <form onSubmit={handleCompleteRegistration}>
                  <div className="alert alert-success" role="alert">
                    ✅ Mobile verified! Please complete your registration.
                  </div>

                  <input
                    type="text"
                    name="name"
                    className="form-control mb-3"
                    placeholder="Full Name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    minLength="2"
                    autoFocus
                  />

                  <input
                    type="email"
                    name="email"
                    className="form-control mb-3"
                    placeholder="Email Address"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />

                  <button
                    type="submit"
                    className="btn btn-secondary w-100 whitT"
                    disabled={loading}
                  >
                    {loading ? "Creating Account..." : "Complete Registration"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-link w-100 mt-2"
                    onClick={handleBackToPhone}
                    disabled={loading}
                  >
                    ← Start Over
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoginRegister;